import { useRef, useState } from 'react';
import { env } from '#lib';
import type {
  PromiseReject,
  PromiseResolve,
  ReCaptchaWidgetElementId
} from '../types';

const SITE_KEY = env.get('VITE_GOOGLE_RECAPTCHA_V2_SITE_KEY');

export const useReCaptchaV2 = () => {
  const [isChallenging, setIsChallenging] = useState(false);

  const ref = useRef<number | undefined>(undefined);
  const promiseResolve = useRef<PromiseResolve>(undefined);
  const promiseReject = useRef<PromiseReject>(undefined);

  const resetInternal = () => {
    setIsChallenging(false);
    promiseResolve.current = undefined;
    promiseReject.current = undefined;
  };

  const reset = () => {
    window.grecaptcha?.reset(ref.current);

    resetInternal();
  };

  const onSuccess = (value: string) => {
    if (promiseResolve.current) {
      promiseResolve.current(value);
    }
    resetInternal();
  };

  const onExpire = () => {
    if (promiseReject.current) {
      promiseReject.current(new Error('reCAPTCHA challenge expired'));
    }

    resetInternal();
  };

  const onError = () => {
    if (promiseReject.current) {
      promiseReject.current(new Error('reCAPTCHA challenge failed to load'));
    }

    resetInternal();
  };

  const init = (elementId: ReCaptchaWidgetElementId) => {
    if (isChallenging) {
      return Promise.reject(new Error('reCAPTCHA challenge already active'));
    }

    return new Promise<string>((resolve, reject) => {
      if (!window.grecaptcha) {
        return reject(new Error('reCAPTCHA failed to load'));
      }

      setIsChallenging(true);

      promiseResolve.current = resolve;
      promiseReject.current = reject;

      window.grecaptcha.ready(() => {
        // Use setTimeout to wait for the next "tick" which
        // allows React to render the div first
        setTimeout(() => {
          try {
            ref.current = window.grecaptcha?.render(elementId, {
              sitekey: SITE_KEY,
              callback: onSuccess,
              'expired-callback': onExpire,
              'error-callback': onError
            });
          } catch (err: any) {
            // FIXME: Log this properly
            console.error(err);

            reject(err);
            resetInternal();
          }
        }, 0);
      });
    });
  };

  return {
    init,
    isChallenging,
    reset
  };
};
